import type {
  BlockPhase,
  EquipmentKind,
  Limitation,
  MusclePriorities,
  Split,
} from '@cadencia/contracts';
import { EXERCISES, findExercise, type Exercise } from '../catalog/exercises.js';
import { exerciseAllowed } from './limitations.js';
import { MUSCLES, type Muscle } from '../catalog/muscles.js';
import type { RepTarget, WeekIntent } from '../load/types.js';
import { priorityFor } from './priorities.js';
import { bandFor } from './volume.js';

/**
 * Maquinaria GENERICA de construccion de bloques
 * (docs/TRAINING_ENGINE.md §3, §4.4 y §7.1).
 *
 * Separa dos ejes que hasta ahora estaban pegados en `readaptation-block.ts`:
 *
 * - el SPLIT decide las plantillas de sesion (que se entrena cada dia);
 * - la FASE decide los parametros de semana (cuantas series, que RIR, que
 *   rangos) y los objetivos y metricas de salida.
 *
 * Son ortogonales: la misma plantilla torso/pierna sirve para readaptacion y
 * para ganancia muscular con parametros distintos. Por eso `buildBlock` recibe
 * los dos por separado y no sabe de ninguna fase concreta.
 */

// ---------------------------------------------------------------------------
// Plantillas
// ---------------------------------------------------------------------------

export type SlotRole = 'main' | 'secondary' | 'accessory';

export interface Slot {
  role: SlotRole;
  /** Preferencias en orden: se elige la primera cuyo equipamiento exista. */
  candidates: string[];
}

export interface SessionTemplate {
  name: string;
  objective: string;
  /** Dia dentro de la semana, 0 = primer dia de entrenamiento. */
  orderInWeek: number;
  slots: Slot[];
}

export interface WeekParameters {
  weekIndex: number;
  intent: WeekIntent;
  mainSets: number;
  accessorySets: number;
  mainTarget: RepTarget;
  accessoryTarget: RepTarget;
}

/**
 * Todo lo que define un bloque antes de resolverlo contra un gimnasio concreto.
 * Es un DATO, no codigo: añadir un split nuevo es añadir plantillas, no tocar
 * `buildBlock`.
 */
export interface BlockTemplate {
  name: string;
  phase: BlockPhase;
  split: Split;
  maxSessionMinutes: number;
  objectives: readonly string[];
  exitMetrics: readonly string[];
  weekParams: readonly WeekParameters[];
  sessions: readonly SessionTemplate[];
}

// ---------------------------------------------------------------------------
// Resultado
// ---------------------------------------------------------------------------

export interface ResolvedExercise {
  slug: string;
  exercise: Exercise;
  role: SlotRole;
  orderIndex: number;
  sets: number;
  target: RepTarget;
  restSeconds: number;
}

export interface ResolvedSession {
  name: string;
  objective: string;
  orderInWeek: number;
  weekIndex: number;
  intent: WeekIntent;
  exercises: ResolvedExercise[];
  estimatedMinutes: number;
  /** Huecos que no se pudieron cubrir con el equipamiento declarado. */
  unresolvedSlots: number;
}

export interface BuildBlockInput {
  /** Tipos de equipamiento realmente disponibles en el gimnasio del usuario. */
  availableEquipmentKinds: readonly EquipmentKind[];
  weeks?: number | undefined;
  /**
   * Prioridades declaradas para el bloque (§4.4). Solo afectan al ORDEN dentro
   * de la sesion; el desplazamiento del objetivo de volumen lo hace
   * `priorities.ts`, que es donde vive esa regla.
   *
   * `| undefined` explicito: con `exactOptionalPropertyTypes` «?» significa
   * AUSENTE, no «presente valiendo undefined», y quien llama suele reenviar un
   * opcional que puede venir sin valor.
   */
  musclePriorities?: MusclePriorities | undefined;
  /**
   * Ejercicios que esta sesion **debe preferir**, si caben.
   *
   * Tiene dos usos y por eso es una lista por sesion y no un mapa por hueco:
   *
   *   1. La seleccion de la IA, ya validada (`validateExerciseSelection`).
   *   2. **Las semanas 2 en adelante de un bloque que ya existe.** Esta es la
   *      que obliga a la forma. `ensureWeekMaterialized` no guarda el bloque:
   *      lo RECONSTRUYE, apoyandose en que `buildBlock` es determinista dado el
   *      equipamiento. Con una eleccion externa esa invariante se rompe, y la
   *      semana 3 saldria con los ejercicios del motor mientras la 1 lleva los
   *      de la IA: un bloque que cambia de ejercicios a mitad sin que nadie lo
   *      pida. Lo unico que se puede recuperar de una semana ya materializada
   *      son los `slug` de sus `session_exercises` —el indice de hueco no
   *      sobrevive al reordenado de `ordenarSesion`—, asi que la lista es lo
   *      que hay, y por eso es lo que se usa tambien en el caso 1.
   *
   * Se consume en el orden de los huecos: cada hueco toma el primer preferido
   * que este entre sus candidatos y no se haya usado ya. Si un mismo ejercicio
   * es candidato de dos huecos, puede acabar en el primero en vez de en el que
   * lo eligio; sigue siendo una sesion valida y completa, y es el precio de que
   * la reconstruccion sea estable.
   *
   * Lo preferido se antepone a los candidatos en vez de sustituirlos, de modo
   * que un `slug` imposible acaba en el ejercicio que habria elegido el motor,
   * nunca en un hueco vacio.
   */
  preferredExercises?: ((sessionName: string) => readonly string[]) | undefined;
  /**
   * Lesiones y limitaciones del atleta (`profiles.limitations`). Lo que vetan
   * no se elige nunca, tampoco si viene como preferido: un ejercicio que la IA
   * o una semana anterior eligieron antes de declarar la lesión no se cuela
   * por la puerta de la reconstrucción.
   */
  limitations?: readonly Limitation[] | undefined;
}

export interface BuiltBlock {
  name: string;
  phase: BlockPhase;
  split: Split;
  plannedWeeks: number;
  sessionsPerWeek: number;
  maxSessionMinutes: number;
  objectives: string[];
  exitMetrics: string[];
  /**
   * Parametros de las semanas efectivamente construidas.
   *
   * Va en el resultado a proposito: antes el repositorio volvia a cortar la
   * tabla de readaptacion por su cuenta (`READAPTATION_WEEKS_PARAMS.slice(...)`),
   * lo que solo funciona mientras exista UNA tabla. Con fases distintas eso
   * seria leer los parametros de otro bloque.
   */
  weekParams: WeekParameters[];
  musclePriorities: MusclePriorities;
  sessions: ResolvedSession[];
  warnings: string[];
}

// ---------------------------------------------------------------------------
// Construccion
// ---------------------------------------------------------------------------

export function pickExercise(
  candidates: readonly string[],
  available: ReadonlySet<EquipmentKind>,
  limitations: readonly Limitation[] = [],
): Exercise | null {
  for (const slug of candidates) {
    const exercise = findExercise(slug);
    if (
      exercise &&
      available.has(exercise.equipmentKind) &&
      exerciseAllowed(exercise, limitations).allowed
    ) {
      return exercise;
    }
  }
  return null;
}

/**
 * Cuando las limitaciones vetan TODOS los candidatos de un hueco.
 *
 * Con la rodilla en «evitar» no queda ninguna prensa ni sentadilla, y dejar el
 * hueco vacío quitaría el cuádriceps de la semana sin decirlo. Se busca en el
 * catálogo entero un ejercicio que entrene el MISMO músculo principal que el
 * primer candidato, que quepa en el gimnasio y que las limitaciones permitan;
 * a igualdad, el de menos carga axial, que es lo prudente al trabajar alrededor
 * de algo que molesta. Si no hay ninguno, el hueco queda sin resolver y el
 * aviso lo dice (docs/TEST_INICIAL.md §2.2).
 *
 * Solo se usa si las limitaciones son la causa: un hueco que se queda vacío
 * por falta de equipamiento sigue avisando como siempre.
 */
export function pickAlternativeForLimitations(
  candidates: readonly string[],
  available: ReadonlySet<EquipmentKind>,
  limitations: readonly Limitation[],
  used: ReadonlySet<string>,
): Exercise | null {
  const referencia = candidates.map((slug) => findExercise(slug)).find((e) => e !== undefined);
  const musculo = referencia?.primaryMuscles[0];
  if (musculo === undefined) return null;
  return (
    EXERCISES.filter(
      (exercise) =>
        exercise.primaryMuscles.includes(musculo) &&
        !used.has(exercise.slug) &&
        available.has(exercise.equipmentKind) &&
        exerciseAllowed(exercise, limitations).allowed,
    ).sort((a, b) => a.axialLoad - b.axialLoad || a.technicalDemand - b.technicalDemand)[0] ?? null
  );
}

/** Minutos aproximados de una sesion: series x (descanso + ~40 s de trabajo). */
export function estimateMinutes(exercises: readonly ResolvedExercise[]): number {
  const seconds = exercises.reduce((total, item) => total + item.sets * (item.restSeconds + 40), 0);
  // Mas un calentamiento general y las aproximaciones.
  return Math.round((seconds + 8 * 60) / 60);
}

const ORDEN_DE_ROL: Record<SlotRole, number> = { main: 0, secondary: 1, accessory: 2 };
const ORDEN_DE_PRIORIDAD: Record<'high' | 'normal' | 'low', number> = {
  high: 0,
  normal: 1,
  low: 2,
};

/**
 * Peso de prioridad de un ejercicio: el mejor (menor) de sus musculos
 * primarios. Un ejercicio que entrena un musculo prioritario adelanta, aunque
 * arrastre otros que no lo son.
 *
 * Los secundarios NO cuentan: si contaran, casi todo empuje adelantaria por el
 * triceps y el orden dejaria de significar nada.
 */
function pesoDePrioridad(exercise: Exercise, priorities: MusclePriorities): number {
  let mejor = ORDEN_DE_PRIORIDAD.low;
  for (const muscle of exercise.primaryMuscles) {
    const peso = ORDEN_DE_PRIORIDAD[priorityFor(muscle as Muscle, priorities)];
    if (peso < mejor) mejor = peso;
  }
  return mejor;
}

/**
 * Ordena los ejercicios de una sesion: primero por rol, luego por prioridad
 * muscular, y a igualdad se conserva el orden de la plantilla.
 *
 * El rol manda SOBRE la prioridad. Un aislamiento de un musculo prioritario no
 * puede desplazar a un basico: hacerlo cambiaria el estimulo del basico (se
 * ejecuta ya fatigado) en lugar de aumentar el del musculo priorizado, que es
 * lo que se pedia. La prioridad reordena DENTRO de cada rol.
 *
 * `Array.prototype.sort` es estable desde ES2019, asi que el desempate por
 * orden de plantilla sale gratis.
 */
function ordenarSesion(
  exercises: readonly ResolvedExercise[],
  priorities: MusclePriorities,
): ResolvedExercise[] {
  return [...exercises]
    .sort((a, b) => {
      const rol = ORDEN_DE_ROL[a.role] - ORDEN_DE_ROL[b.role];
      if (rol !== 0) return rol;
      return pesoDePrioridad(a.exercise, priorities) - pesoDePrioridad(b.exercise, priorities);
    })
    .map((item, index) => ({ ...item, orderIndex: index + 1 }));
}

/**
 * Series mínimas por rol al recortar por tiempo. Un principal con una serie ya
 * no es un principal, y por eso se detiene en dos.
 */
export const SERIES_MINIMAS_AL_RECORTAR: Record<SlotRole, number> = {
  main: 2,
  secondary: 2,
  accessory: 1,
};

/**
 * Quita series hasta que la sesión quepa en su tiempo. Devuelve cuántas quitó.
 *
 * Hasta el test inicial solo se avisaba: «no se recorta automáticamente,
 * quitar series sin decirlo cambiaría el volumen». Con una duración que el
 * atleta DECLARA, el límite pasa a ser duro —una sesión de 80 minutos para
 * alguien que tiene 60 no se hace, se abandona a medias—, así que se recorta y
 * se dice (el aviso lo compone `buildBlock`).
 *
 * El orden es el de menos a más importante: primero el accesorio con más
 * series, luego el secundario, y el principal el último. Muta `exercises`.
 */
function recortarATiempo(exercises: ResolvedExercise[], maxMinutes: number): number {
  let quitadas = 0;
  const orden: SlotRole[] = ['accessory', 'secondary', 'main'];
  while (estimateMinutes(exercises) > maxMinutes) {
    let victima: ResolvedExercise | undefined;
    for (const rol of orden) {
      victima = exercises
        .filter((e) => e.role === rol && e.sets > SERIES_MINIMAS_AL_RECORTAR[rol])
        .sort((a, b) => b.sets - a.sets)[0];
      if (victima !== undefined) break;
    }
    // Ya está todo en su mínimo: no cabe, y el aviso de minutos lo dirá.
    if (victima === undefined) break;
    victima.sets -= 1;
    quitadas += 1;
  }
  return quitadas;
}

/**
 * Resuelve una plantilla de bloque contra el equipamiento real del usuario.
 *
 * No falla nunca por falta de material: los huecos que no se pueden cubrir se
 * cuentan en `unresolvedSlots` y se avisan en `warnings`. Un bloque con seis
 * ejercicios de siete es utilizable; uno que lanza una excepcion, no.
 */
export function buildBlock(template: BlockTemplate, input: BuildBlockInput): BuiltBlock {
  const available = new Set(input.availableEquipmentKinds);
  // El peso corporal siempre esta disponible.
  available.add('bodyweight');

  const priorities = input.musclePriorities ?? {};
  const limitations = input.limitations ?? [];
  const weeks = Math.min(input.weeks ?? template.weekParams.length, template.weekParams.length);
  const weekParams = template.weekParams.slice(0, Math.max(weeks, 0));

  const warnings: string[] = [];
  const sessions: ResolvedSession[] = [];
  // Máximo de series quitadas por tiempo a cada sesión, en cualquier semana.
  const recortesPorTiempo = new Map<string, number>();

  for (const params of weekParams) {
    for (const sessionTemplate of template.sessions) {
      const exercises: ResolvedExercise[] = [];
      let unresolved = 0;
      let porLimitaciones = 0;

      // Los preferidos que quedan por colocar en ESTA sesion. Se van gastando:
      // sin eso, un ejercicio candidato de tres huecos ocuparia los tres.
      const preferidos = new Set(input.preferredExercises?.(sessionTemplate.name) ?? []);

      for (const slot of sessionTemplate.slots) {
        const preferido = slot.candidates.find((slug) => preferidos.has(slug));
        const candidatos = preferido === undefined ? slot.candidates : [preferido, ...slot.candidates];
        let exercise = pickExercise(candidatos, available, limitations);
        // Si sin las limitaciones SÍ habría ejercicio, son ellas las que han
        // vaciado el hueco: se busca otro del mismo músculo antes de rendirse.
        if (!exercise && limitations.length > 0 && pickExercise(candidatos, available) !== null) {
          exercise = pickAlternativeForLimitations(
            slot.candidates,
            available,
            limitations,
            new Set(exercises.map((e) => e.slug)),
          );
          if (!exercise) porLimitaciones += 1;
        }
        if (exercise) preferidos.delete(exercise.slug);
        if (!exercise) {
          unresolved += 1;
          continue;
        }
        const isMain = slot.role === 'main';
        exercises.push({
          slug: exercise.slug,
          exercise,
          role: slot.role,
          // Provisional: `ordenarSesion` lo reasigna tras ordenar.
          orderIndex: exercises.length + 1,
          sets: isMain ? params.mainSets : params.accessorySets,
          target: isMain ? params.mainTarget : params.accessoryTarget,
          restSeconds: exercise.defaultRestSeconds,
        });
      }

      if (porLimitaciones > 0) {
        warnings.push(
          `${sessionTemplate.name} (semana ${params.weekIndex}): ${porLimitaciones} hueco(s) sin ejercicio compatible con tus limitaciones`,
        );
      }
      if (unresolved - porLimitaciones > 0) {
        warnings.push(
          `${sessionTemplate.name} (semana ${params.weekIndex}): ${unresolved - porLimitaciones} hueco(s) sin equipamiento disponible`,
        );
      }

      const ordenados = ordenarSesion(exercises, priorities);
      const recortadas = recortarATiempo(ordenados, template.maxSessionMinutes);
      if (recortadas > 0) {
        recortesPorTiempo.set(
          sessionTemplate.name,
          Math.max(recortesPorTiempo.get(sessionTemplate.name) ?? 0, recortadas),
        );
      }

      sessions.push({
        name: sessionTemplate.name,
        objective: sessionTemplate.objective,
        orderInWeek: sessionTemplate.orderInWeek,
        weekIndex: params.weekIndex,
        intent: params.intent,
        exercises: ordenados,
        estimatedMinutes: estimateMinutes(ordenados),
        unresolvedSlots: unresolved,
      });
    }
  }

  for (const [nombre, series] of recortesPorTiempo) {
    warnings.push(
      `${nombre}: hasta ${String(series)} serie${series === 1 ? '' : 's'} menos para caber en ${String(template.maxSessionMinutes)} min`,
    );
  }

  warnings.push(...avisosDeVolumen(sessions, weekParams));

  const excedidas = [
    ...new Set(
      sessions
        .filter((session) => session.estimatedMinutes > template.maxSessionMinutes)
        .map((session) => session.name),
    ),
  ];
  if (excedidas.length > 0) {
    // No se recorta automaticamente: quitar series sin decirlo cambiaria el
    // volumen semanal a espaldas del motor de volumen (§4).
    warnings.push(
      `Sesiones por encima de ${template.maxSessionMinutes} min: ${excedidas.join(', ')}`,
    );
  }

  return {
    name: template.name,
    phase: template.phase,
    split: template.split,
    plannedWeeks: weekParams.length,
    sessionsPerWeek: template.sessions.length,
    maxSessionMinutes: template.maxSessionMinutes,
    objectives: [...template.objectives],
    exitMetrics: [...template.exitMetrics],
    weekParams,
    musclePriorities: priorities,
    sessions,
    warnings,
  };
}

/**
 * Avisa de los musculos cuyo volumen semanal se sale por ARRIBA de su banda
 * (§4.2), semana a semana.
 *
 * Avisa y no recorta, a proposito. Las bandas del §4.2 son un punto de partida
 * que el sistema recalibra con la respuesta observada (§4.3); recortar series
 * aqui en silencio pisaria esa recalibracion y ademas escondaria el verdadero
 * problema, que casi siempre es una plantilla con demasiados huecos del mismo
 * musculo. El limite duro —±20 % de cambio semanal— vive en `safety/limits.ts`
 * y lo aplica el motor de volumen, no el generador.
 *
 * No se avisa por defecto de volumen: una readaptacion esta por debajo de la
 * banda de forma deliberada (§3), asi que un aviso «corto» seria ruido en la
 * fase donde estar corto es el plan.
 */
function avisosDeVolumen(
  sessions: readonly ResolvedSession[],
  weekParams: readonly WeekParameters[],
): string[] {
  const salida: string[] = [];
  for (const params of weekParams) {
    const volumen = weeklyVolumeByMuscle(
      sessions.filter((session) => session.weekIndex === params.weekIndex),
    );
    const pasados = MUSCLES.filter(
      (muscle) => (volumen.get(muscle) ?? 0) > bandFor(muscle).max,
    ).map((muscle) => `${muscle} (${volumen.get(muscle) ?? 0} > ${bandFor(muscle).max})`);

    if (pasados.length > 0) {
      salida.push(`Semana ${params.weekIndex}: volumen por encima de la banda en ${pasados.join(', ')}`);
    }
  }
  return salida;
}

/** Series efectivas por musculo y semana, contando secundarios a 0,5. */
export function weeklyVolumeByMuscle(sessions: readonly ResolvedSession[]): Map<string, number> {
  const volume = new Map<string, number>();
  for (const session of sessions) {
    for (const item of session.exercises) {
      for (const muscle of item.exercise.primaryMuscles) {
        volume.set(muscle, (volume.get(muscle) ?? 0) + item.sets);
      }
      for (const muscle of item.exercise.secondaryMuscles) {
        volume.set(muscle, (volume.get(muscle) ?? 0) + item.sets * 0.5);
      }
    }
  }
  return volume;
}
